import { Fragment, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import Layout from '../components/Layout';
import RouteTitleHandler from '../components/common/RouteTitleHandler';
import Modal from '../components/common/Modal';
import { useModuleContext } from '../context/ModuleContext';
import { fetchUsage } from '../services/usageService';
import {
  CUSTOMER_CHART_TOP_N,
  aggregateUsageCustomers,
  aggregateUsageUsers,
  padUsageByDate,
} from '../utils/usageTransforms';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, Building2, Calendar, ChevronDown, Maximize2, Users, X } from 'lucide-react';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const VIEW_TABS = [
  { id: 'daily', label: 'Daily' },
  { id: 'last7', label: 'Last 7 Days' },
  { id: 'calendarMonth', label: 'Calendar Month' },
  { id: 'month', label: 'Custom' },
];

/**
 * Same values and order as `defaultColors` in `components/DynamicChart.jsx`
 * (series / pie slice palette). Keep the two lists aligned if you change colors.
 */
const DYNAMIC_CHART_DEFAULT_COLORS = [
  '#FF6B9D',
  '#FF8F5A',
  '#FFD93D',
  '#6BCF7F',
  '#4D96FF',
  '#9B59B6',
  '#E74C3C',
  '#F39C12',
  '#2ECC71',
  '#3498DB',
  '#9B59B6',
  '#1ABC9C',
  '#E67E22',
  '#34495E',
  '#95A5A6',
];

const PIE_SLICE_COLORS = DYNAMIC_CHART_DEFAULT_COLORS;

/** Bar fills derived from the same palette indices as DynamicChart-style charts */
const INSIGHTS_BAR_GRADIENT = {
  from: DYNAMIC_CHART_DEFAULT_COLORS[3],
  to: DYNAMIC_CHART_DEFAULT_COLORS[8],
};
const CUSTOMER_BAR_GRADIENT = {
  from: DYNAMIC_CHART_DEFAULT_COLORS[4],
  to: DYNAMIC_CHART_DEFAULT_COLORS[13],
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Calendar month (1–12) and inclusive date range for that month in local time. */
function getCurrentCalendarMonthState() {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const last = new Date(y, m, 0).getDate();
  return {
    month: m,
    year: y,
    dateFrom: `${y}-${pad2(m)}-01`,
    dateTo: `${y}-${pad2(m)}-${pad2(last)}`,
  };
}

function formatYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Add calendar days to a YYYY-MM-DD string (local date). */
function addCalendarDaysFromYMD(ymdStr, deltaDays) {
  const [y, m, d] = ymdStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d, 12, 0, 0, 0);
  dt.setDate(dt.getDate() + deltaDays);
  return formatYMD(dt);
}

const USAGE_FILTER_CONTROL =
  'rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-white/30 disabled:opacity-50';

const chartTooltipStyle = {
  backgroundColor: '#1F2937',
  border: '1px solid #4B5563',
  borderRadius: 8,
  color: '#E5E7EB',
};

const pieTooltipLabelStyle = { color: '#E5E7EB', fontWeight: 600 };
const pieTooltipItemStyle = { color: '#E5E7EB' };

function ChartCard({
  title,
  children,
  isLoading,
  error,
  headerAction = null,
  chartHeightClass = 'h-[260px]',
  titleClassName = 'text-sm',
  showTitle = true,
}) {
  return (
    <div className="rounded-xl border border-white/15 bg-black/30 p-4 shadow-lg backdrop-blur-md">
      {showTitle ? (
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className={`font-semibold text-gray-100 ${titleClassName}`}>{title}</h3>
          {headerAction}
        </div>
      ) : null}
      <div className={`relative w-full min-w-0 ${chartHeightClass}`}>
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-slate-900/85 text-sm text-gray-300">
            Loading…
          </div>
        ) : null}
        {error && !isLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-slate-900/85 px-4 text-center text-sm text-red-300">
            {error}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, accent }) {
  return (
    <div className="rounded-xl border border-white/15 bg-black/30 p-4 shadow-md backdrop-blur-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-semibold tabular-nums text-gray-100">{value}</p>
        </div>
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${accent}`}
        >
          <Icon className="h-5 w-5 text-white" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function InsightsGeneratedChart({ data, axisTick, gridStroke }) {
  const gradientId = useId().replace(/:/g, '');
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={INSIGHTS_BAR_GRADIENT.from} stopOpacity={0.95} />
            <stop offset="100%" stopColor={INSIGHTS_BAR_GRADIENT.to} stopOpacity={0.9} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
        <XAxis dataKey="day" tick={axisTick} axisLine={{ stroke: gridStroke }} />
        <YAxis tick={axisTick} axisLine={{ stroke: gridStroke }} allowDecimals={false} />
        <Tooltip contentStyle={chartTooltipStyle} />
        <Bar dataKey="insights" fill={`url(#${gradientId})`} radius={[4, 4, 0, 0]} name="Insights" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ReportsByUserChart({ userPieData, isLoading, error, isExpanded = false }) {
  if (userPieData.length === 0 && !isLoading && !error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        No sessions attributed to users in this range
      </div>
    );
  }

  if (error) {
    return <div className="h-full min-h-[120px]" aria-hidden />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={userPieData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy={isExpanded ? '44%' : '46%'}
          innerRadius={isExpanded ? '32%' : 58}
          outerRadius={isExpanded ? '62%' : 88}
          paddingAngle={2}
          rootTabIndex={-1}
          className="outline-none focus:outline-none focus-visible:outline-none [&_.recharts-pie-sector]:outline-none [&_.recharts-pie-sector:focus]:outline-none [&_.recharts-pie-sector:focus-visible]:outline-none"
        >
          {userPieData.map((entry, i) => (
            <Cell key={entry.key} fill={PIE_SLICE_COLORS[i % PIE_SLICE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={chartTooltipStyle}
          labelStyle={pieTooltipLabelStyle}
          itemStyle={pieTooltipItemStyle}
        />
        <Legend
          verticalAlign="bottom"
          iconSize={isExpanded ? 12 : 10}
          wrapperStyle={{
            fontSize: isExpanded ? 13 : 11,
            paddingTop: isExpanded ? 14 : 8,
            paddingBottom: isExpanded ? 12 : 10,
          }}
          formatter={(value) => <span className="text-gray-300">{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function ReportsByCustomerChart({
  customerChartData,
  customerRemainder,
  isLoading,
  error,
  axisTick,
  gridStroke,
  onOpenRemainder,
  isExpanded = false,
}) {
  const gradientId = useId().replace(/:/g, '');

  if (customerChartData.length === 0 && !isLoading && !error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        No sessions attributed to customers in this range
      </div>
    );
  }

  if (error) {
    return <div className="h-full min-h-[120px]" aria-hidden />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart layout="vertical" data={customerChartData} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={CUSTOMER_BAR_GRADIENT.from} />
                <stop offset="100%" stopColor={CUSTOMER_BAR_GRADIENT.from} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} />
            <XAxis type="number" tick={axisTick} axisLine={{ stroke: gridStroke }} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={isExpanded ? 220 : 148}
              tick={{ ...axisTick, fontSize: isExpanded ? 12 : 10 }}
              axisLine={{ stroke: gridStroke }}
            />
            <Tooltip contentStyle={chartTooltipStyle} />
            <Bar
              dataKey="value"
              fill={`url(#${gradientId})`}
              radius={[0, 4, 4, 0]}
              name="Reports"
              onClick={(barEntry) => {
                if (isLoading || error || customerRemainder.length === 0) return;
                const row = barEntry?.payload ?? barEntry;
                if (row?.name === 'Other') onOpenRemainder();
              }}
              cursor={customerRemainder.length > 0 ? 'pointer' : 'default'}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {customerRemainder.length > 0 ? (
        <button
          type="button"
          onClick={onOpenRemainder}
          className={`shrink-0 text-left font-medium text-blue-400 hover:text-blue-300 ${
            isExpanded ? 'text-sm' : 'text-xs'
          }`}
        >
          View {customerRemainder.length} additional customer
          {customerRemainder.length === 1 ? '' : 's'} (rolled into Other)
        </button>
      ) : null}
    </div>
  );
}

export default function UsageAnalytics() {
  const { getApplicationIdForModule, isLoadingApplications } = useModuleContext();
  const [viewTab, setViewTab] = useState('calendarMonth');
  const [calendarBaseline] = useState(() => getCurrentCalendarMonthState());
  const [month, setMonth] = useState(calendarBaseline.month);
  const [year, setYear] = useState(calendarBaseline.year);
  const [dateFrom, setDateFrom] = useState(calendarBaseline.dateFrom);
  const [dateTo, setDateTo] = useState(calendarBaseline.dateTo);
  const [usagePayload, setUsagePayload] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(null);
  const [customerRemainderOpen, setCustomerRemainderOpen] = useState(false);
  const [expandedChartId, setExpandedChartId] = useState(null);

  const focusApplicationId = getApplicationIdForModule('focus360');

  const applyCalendarMonth = useCallback((m, y) => {
    const last = new Date(y, m, 0).getDate();
    setDateFrom(`${y}-${pad2(m)}-01`);
    setDateTo(`${y}-${pad2(m)}-${pad2(last)}`);
  }, []);

  const handleTabChange = (tab) => {
    setViewTab(tab);
    const today = new Date();
    if (tab === 'calendarMonth') {
      applyCalendarMonth(month, year);
    } else if (tab === 'last7') {
      const end = new Date(today);
      const start = new Date(today);
      start.setDate(end.getDate() - 6);
      setDateFrom(formatYMD(start));
      setDateTo(formatYMD(end));
    } else if (tab === 'month') {
      const end = new Date(today);
      const start = new Date(today);
      start.setDate(end.getDate() - 29);
      setDateFrom(formatYMD(start));
      setDateTo(formatYMD(end));
    } else if (tab === 'daily') {
      setDateTo(dateFrom);
    }
  };

  useEffect(() => {
    if (viewTab === 'calendarMonth') {
      applyCalendarMonth(month, year);
    }
  }, [month, year, viewTab, applyCalendarMonth]);

  useEffect(() => {
    if (viewTab === 'daily') {
      setDateTo(dateFrom);
    }
  }, [dateFrom, viewTab]);

  useEffect(() => {
    if (isLoadingApplications) return;
    if (!focusApplicationId) {
      setUsageLoading(false);
      setUsagePayload(null);
      setUsageError('Focus 360 application is not configured.');
      return;
    }
    let cancelled = false;
    setUsageLoading(true);
    setUsageError(null);
    fetchUsage(focusApplicationId, dateFrom, dateTo)
      .then((body) => {
        if (cancelled) return;
        if (body?.status === 'ok' && body.data) {
          setUsagePayload(body.data);
        } else {
          setUsagePayload(null);
          setUsageError('Unexpected usage API response.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setUsagePayload(null);
        const detail = err.response?.data?.detail;
        setUsageError(
          typeof detail === 'string' ? detail : err.message || 'Failed to load usage data.',
        );
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, focusApplicationId, isLoadingApplications]);

  const usageByDate = usagePayload?.chart?.by_date;

  const apiDailySeries = useMemo(
    () => padUsageByDate(usageByDate || [], dateFrom, dateTo),
    [usageByDate, dateFrom, dateTo],
  );

  const userPieData = useMemo(
    () => aggregateUsageUsers(usageByDate || []),
    [usageByDate],
  );

  const { chartData: customerChartData, remainder: customerRemainder } = useMemo(
    () => aggregateUsageCustomers(usageByDate || []),
    [usageByDate],
  );

  const apiChartsLoading = isLoadingApplications || usageLoading;

  const apiChartError =
    !isLoadingApplications && !focusApplicationId
      ? 'Focus 360 application is not configured.'
      : usageError;

  const usageKpis = usagePayload?.kpis;

  const kpiDisplay = useMemo(() => {
    const dash = '—';
    const hasKpis = usageKpis != null;
    const placeholder =
      (apiChartsLoading && !hasKpis) || (apiChartError && !hasKpis);
    if (placeholder) {
      return { sessionCount: dash, uniqueUsers: dash, uniqueCustomers: dash };
    }
    return {
      sessionCount: String(usageKpis?.session_count ?? 0),
      uniqueUsers: String(usageKpis?.unique_users ?? 0),
      uniqueCustomers: String(usageKpis?.unique_customers ?? 0),
    };
  }, [apiChartsLoading, apiChartError, usageKpis]);

  const axisTick = { fill: '#E5E7EB', fontSize: 11 };
  const expandedAxisTick = { fill: '#E5E7EB', fontSize: 13 };
  const gridStroke = '#4B5563';

  const generatedAt = useMemo(() => new Date().toISOString().slice(0, 19).replace('T', ' '), []);
  const expandedChartOpen = expandedChartId != null;

  const expandedChartTitle = useMemo(() => {
    if (expandedChartId === 'insights') return 'Insights generated per day';
    if (expandedChartId === 'users') return 'Reports generated by user';
    if (expandedChartId === 'customers') return 'Report generated - Customer wise';
    return 'Usage chart';
  }, [expandedChartId]);

  const periodLabelId = useId();
  const periodButtonText = useMemo(
    () => VIEW_TABS.find((t) => t.id === viewTab)?.label ?? viewTab,
    [viewTab],
  );

  return (
    <Layout>
      <RouteTitleHandler title="Focus 360 — Usage Analytics" />
      <div className="flex w-full min-h-0 flex-1 flex-col overflow-auto text-white">
        <div className="mx-auto w-full max-w-[1600px] px-3 pb-8 pt-2 sm:px-6">
          <section className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-lg border border-gray-700/50 bg-gray-800/50 p-4 backdrop-blur-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span id={periodLabelId} className="shrink-0 text-md font-medium whitespace-nowrap text-gray-400">
                Period
              </span>
              <Listbox value={viewTab} onChange={handleTabChange}>
                <div className="relative min-w-0">
                  <ListboxButton
                    aria-labelledby={periodLabelId}
                    className={`inline-flex min-w-[10rem] max-w-[min(100vw-8rem,16rem)] items-center justify-between gap-2 ${USAGE_FILTER_CONTROL}`}
                  >
                    <span className="truncate text-left">{periodButtonText}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  </ListboxButton>
                  <ListboxOptions
                    anchor="bottom end"
                    transition
                    className="z-50 mt-1 max-h-60 min-w-[10rem] overflow-auto rounded-lg border border-white/20 bg-slate-900/95 py-1 text-sm shadow-xl outline-none backdrop-blur-md [--anchor-gap:4px] data-[closed]:opacity-0 data-[enter]:transition data-[enter]:duration-100 data-[leave]:duration-75 data-[leave]:ease-in"
                  >
                    {VIEW_TABS.map((t) => (
                      <ListboxOption key={t.id} value={t.id} className="flex cursor-pointer rounded-md px-1 py-0.5">
                        {({ selected, active }) => (
                          <span
                            className={`w-full rounded-md px-2 py-2 transition-colors ${
                              active ? 'bg-white/15 text-white' : 'text-gray-200'
                            } ${selected ? 'bg-blue-600/40 font-medium text-white' : ''}`}
                          >
                            {t.label}
                          </span>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>

            {viewTab === 'calendarMonth' ? (
              <>
                <label className="flex min-w-0 items-center gap-2 text-md text-gray-400">
                  <span className="shrink-0 font-medium whitespace-nowrap">Month</span>
                  <select
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    className={USAGE_FILTER_CONTROL}
                  >
                    {MONTH_LABELS.map((label, idx) => (
                      <option key={label} value={idx + 1} className="bg-slate-800 text-gray-100">
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex min-w-0 items-center gap-2 text-md text-gray-400">
                  <span className="shrink-0 font-medium whitespace-nowrap">Year</span>
                  <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className={USAGE_FILTER_CONTROL}
                  >
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <option key={y} value={y} className="bg-slate-800 text-gray-100">
                        {y}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}

            {viewTab === 'daily' ? (
              <label className="flex min-w-0 items-center gap-2 text-md text-gray-400">
                <span className="inline-flex shrink-0 items-center gap-1 font-medium whitespace-nowrap">
                  <Calendar className="h-3.5 w-3.5" aria-hidden />
                  Date
                </span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDateFrom(v);
                    setDateTo(v);
                  }}
                  className={USAGE_FILTER_CONTROL}
                />
              </label>
            ) : null}

            {viewTab === 'last7' || viewTab === 'month' ? (
              <>
                <label className="flex min-w-0 items-center gap-2 text-md text-gray-400">
                  <span className="inline-flex shrink-0 items-center gap-1 font-medium whitespace-nowrap">
                    <Calendar className="h-3.5 w-3.5" aria-hidden />
                    From
                  </span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      const nextFrom = e.target.value;
                      if (viewTab === 'last7') {
                        setDateFrom(nextFrom);
                        setDateTo(addCalendarDaysFromYMD(nextFrom, 6));
                      } else {
                        setDateFrom(nextFrom);
                        setDateTo(addCalendarDaysFromYMD(nextFrom, 29));
                      }
                    }}
                    className={USAGE_FILTER_CONTROL}
                  />
                </label>
                <label className="flex min-w-0 items-center gap-2 text-md text-gray-400">
                  <span className="inline-flex shrink-0 items-center gap-1 font-medium whitespace-nowrap">
                    <Calendar className="h-3.5 w-3.5" aria-hidden />
                    To
                  </span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={USAGE_FILTER_CONTROL}
                  />
                </label>
              </>
            ) : null}
          </section>

          <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard
              title="Insights"
              value={kpiDisplay.sessionCount}
              icon={Activity}
              accent="bg-sky-600"
            />
            <KpiCard
              title="Unique users"
              value={kpiDisplay.uniqueUsers}
              icon={Users}
              accent="bg-teal-600"
            />
            <KpiCard
              title="Unique customers"
              value={kpiDisplay.uniqueCustomers}
              icon={Building2}
              accent="bg-emerald-600"
            />
          </section>

          {/* Main charts: two columns, then full-width customer row */}
          <section className="mt-6 grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="Insights generated per day"
              isLoading={apiChartsLoading}
              error={apiChartError}
              headerAction={
                <button
                  type="button"
                  onClick={() => setExpandedChartId('insights')}
                  className="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-xs font-medium text-gray-200 transition-colors hover:border-white/35 hover:bg-white/10 hover:text-white"
                >
                  <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                  Expand
                </button>
              }
            >
              <InsightsGeneratedChart data={apiDailySeries} axisTick={axisTick} gridStroke={gridStroke} />
            </ChartCard>

            <ChartCard
              title="Reports generated by user"
              isLoading={apiChartsLoading}
              error={apiChartError}
              headerAction={
                <button
                  type="button"
                  onClick={() => setExpandedChartId('users')}
                  className="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-xs font-medium text-gray-200 transition-colors hover:border-white/35 hover:bg-white/10 hover:text-white"
                >
                  <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                  Expand
                </button>
              }
            >
              <ReportsByUserChart
                userPieData={userPieData}
                isLoading={apiChartsLoading}
                error={apiChartError}
              />
            </ChartCard>

            <div className="min-w-0 lg:col-span-2">
              <ChartCard
                title="Report generated - Customer wise"
                isLoading={apiChartsLoading}
                error={apiChartError}
                headerAction={
                  <button
                    type="button"
                    onClick={() => setExpandedChartId('customers')}
                    className="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-xs font-medium text-gray-200 transition-colors hover:border-white/35 hover:bg-white/10 hover:text-white"
                  >
                    <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                    Expand
                  </button>
                }
              >
                <ReportsByCustomerChart
                  customerChartData={customerChartData}
                  customerRemainder={customerRemainder}
                  isLoading={apiChartsLoading}
                  error={apiChartError}
                  axisTick={axisTick}
                  gridStroke={gridStroke}
                  onOpenRemainder={() => setCustomerRemainderOpen(true)}
                />
              </ChartCard>
            </div>
          </section>

          <Modal
            open={expandedChartOpen}
            setOpen={() => setExpandedChartId(null)}
            modalWidth="min(96vw, 1280px)"
            isDark
          >
            <Fragment>
              <div className="flex items-start justify-between gap-3 border-b border-white/15 p-4">
                <h2 className="text-lg font-semibold text-gray-100">{expandedChartTitle}</h2>
                <button
                  type="button"
                  onClick={() => setExpandedChartId(null)}
                  className="flex size-8 shrink-0 items-center justify-center rounded text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="p-4 sm:p-5">
                <ChartCard
                  title={expandedChartTitle}
                  isLoading={apiChartsLoading}
                  error={apiChartError}
                  chartHeightClass="h-[72vh] min-h-[420px] max-h-[78vh]"
                  titleClassName="text-base"
                  showTitle={false}
                >
                  {expandedChartId === 'insights' ? (
                    <InsightsGeneratedChart
                      data={apiDailySeries}
                      axisTick={expandedAxisTick}
                      gridStroke={gridStroke}
                    />
                  ) : null}
                  {expandedChartId === 'users' ? (
                    <ReportsByUserChart
                      userPieData={userPieData}
                      isLoading={apiChartsLoading}
                      error={apiChartError}
                      isExpanded
                    />
                  ) : null}
                  {expandedChartId === 'customers' ? (
                    <ReportsByCustomerChart
                      customerChartData={customerChartData}
                      customerRemainder={customerRemainder}
                      isLoading={apiChartsLoading}
                      error={apiChartError}
                      axisTick={expandedAxisTick}
                      gridStroke={gridStroke}
                      onOpenRemainder={() => setCustomerRemainderOpen(true)}
                      isExpanded
                    />
                  ) : null}
                </ChartCard>
              </div>
            </Fragment>
          </Modal>

          <Modal
            open={customerRemainderOpen}
            setOpen={() => setCustomerRemainderOpen(false)}
            modalWidth="560px"
            isDark
          >
            <Fragment>
              <div className="flex items-start justify-between gap-3 border-b border-white/15 p-4">
                <h2 className="text-lg font-semibold text-gray-100">
                  Additional customers (beyond top {CUSTOMER_CHART_TOP_N})
                </h2>
                <button
                  type="button"
                  onClick={() => setCustomerRemainderOpen(false)}
                  className="flex size-8 shrink-0 items-center justify-center rounded text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="max-h-[60vh] overflow-auto p-4">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-600 text-left text-xs uppercase tracking-wide text-gray-400">
                      <th className="py-2 pr-4 font-medium">Customer</th>
                      <th className="py-2 text-right font-medium tabular-nums">Reports</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerRemainder.map((row) => (
                      <tr key={row.name} className="border-b border-slate-700/80 text-gray-200">
                        <td className="py-2 pr-4 text-gray-100">{row.name}</td>
                        <td className="py-2 text-right tabular-nums text-gray-300">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Fragment>
          </Modal>

          <footer className="mt-8 flex flex-col gap-1 border-t border-white/15 pt-4 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <span>Focus 360 — Usage Analytics Dashboard</span>
            <span className="tabular-nums">
              Data range {dateFrom} — {dateTo}
              <span className="mx-2 text-gray-600">|</span>
              Generated {generatedAt}
            </span>
          </footer>
        </div>
      </div>
    </Layout>
  );
}
